import type { SVGProps } from 'react';
import {
  driveFileIconKind,
  type DriveBrowserItem,
  type DriveFileIconKind,
} from '../lib/drive-browser';

type DriveFileIconProps = Omit<SVGProps<SVGSVGElement>, 'children'> & {
  item: Pick<DriveBrowserItem, 'mimeType' | 'name'>;
  size?: number;
};

const colors: Record<Exclude<DriveFileIconKind, 'folder'>, string> = {
  'google-sheets': '#0f9d58',
  excel: '#217346',
  'google-docs': '#4285f4',
  word: '#2b579a',
  'google-slides': '#f9ab00',
  powerpoint: '#d24726',
  pdf: '#ea4335',
  image: '#7e57c2',
  archive: '#6b7785',
  audio: '#a142f4',
  video: '#e91e63',
  text: '#607d8b',
  file: '#78909c',
};

function FileGlyph({ kind }: { kind: Exclude<DriveFileIconKind, 'folder'> }) {
  if (kind === 'google-sheets') {
    return (
      <g fill="none" stroke="#fff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25">
        <rect x="8.2" y="9.1" width="7.6" height="7.1" rx=".45" />
        <path d="M8.2 11.45h7.6M8.2 13.82h7.6M11.95 9.1v7.1" />
      </g>
    );
  }

  if (kind === 'google-docs') {
    return (
      <g fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="1.35">
        <path d="M8.6 10h6.8M8.6 12.8h6.8M8.6 15.6h4.8" />
      </g>
    );
  }

  if (kind === 'google-slides') {
    return (
      <g fill="none" stroke="#fff" strokeLinejoin="round" strokeWidth="1.35">
        <rect x="8.15" y="9.45" width="7.7" height="6.35" rx=".55" />
        <path d="M10.35 12.62h3.3" strokeLinecap="round" />
      </g>
    );
  }

  if (kind === 'image') {
    return (
      <g fill="none" stroke="#fff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25">
        <rect x="7.9" y="9.1" width="8.2" height="7.15" rx=".65" />
        <circle cx="10.35" cy="11.35" r=".7" fill="#fff" stroke="none" />
        <path d="m8.65 15.4 2.05-2.05 1.45 1.25 1.3-1.1 1.9 1.9" />
      </g>
    );
  }

  if (kind === 'archive') {
    return (
      <g fill="#fff">
        <rect x="10.8" y="8.65" width="2.4" height="1.35" rx=".25" />
        <rect x="10.8" y="10.7" width="2.4" height="1.35" rx=".25" />
        <rect x="10.8" y="12.75" width="2.4" height="1.35" rx=".25" />
        <path d="M10.35 15.1h3.3v1.55h-3.3z" />
      </g>
    );
  }

  if (kind === 'audio') {
    return <path d="M10.6 9.2v5.25a1.72 1.72 0 1 0 1.2 1.64v-4.48l4-1.02v2.84a1.72 1.72 0 1 0 1.2 1.64V8.55z" fill="#fff" />;
  }

  if (kind === 'video') {
    return (
      <g fill="none" stroke="#fff" strokeLinejoin="round" strokeWidth="1.25">
        <rect x="7.9" y="9.5" width="8.2" height="6.4" rx=".75" />
        <path d="m11.15 11.25 2.85 1.45-2.85 1.45z" fill="#fff" stroke="none" />
      </g>
    );
  }

  if (kind === 'text' || kind === 'file') {
    return (
      <g fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="1.35">
        <path d="M8.7 10h6.6M8.7 12.8h6.6M8.7 15.6h4.6" />
      </g>
    );
  }

  const abbreviation = {
    excel: 'X',
    word: 'W',
    powerpoint: 'P',
    pdf: 'PDF',
  }[kind];

  return (
    <text
      x="12"
      y="15.75"
      fill="#fff"
      fontFamily="Arial, sans-serif"
      fontSize={kind === 'pdf' ? 5.4 : 8.4}
      fontWeight="700"
      textAnchor="middle"
    >
      {abbreviation}
    </text>
  );
}

export function DriveFileIcon({ item, size = 20, className, ...props }: DriveFileIconProps) {
  const detectedKind = driveFileIconKind(item);
  const kind = detectedKind === 'folder' ? 'file' : detectedKind;
  const classes = ['drive-file-icon', `drive-file-icon-${kind}`, className].filter(Boolean).join(' ');

  return (
    <svg
      {...props}
      aria-hidden="true"
      className={classes}
      focusable="false"
      height={size}
      viewBox="0 0 24 24"
      width={size}
    >
      <path
        d="M7.15 2.25h6.7l4.9 4.9v12.6a2 2 0 0 1-2 2h-9.6a2 2 0 0 1-2-2V4.25a2 2 0 0 1 2-2Z"
        fill={colors[kind]}
      />
      <path d="M13.85 2.25v3.4a1.5 1.5 0 0 0 1.5 1.5h3.4Z" fill="#fff" opacity=".72" />
      <FileGlyph kind={kind} />
    </svg>
  );
}
